import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  TaskName,
  useUpdateCarTrailerDetailsMutation,
  useUpdateExtraColumnsMutation,
  useUpdateLegDetailsMutation,
  useUpdateVolumeDetailsMutation,
  useTerminalStatusTruckFillAndTimeUpdateSubscription,
} from '../../../../generated/graphql';
import { useUserConfiguration } from '../../../../providers/UserConfigurationProvider';
import { format, parseISO } from 'date-fns';
import { GridCellParams } from '@mui/x-data-grid-pro';

// ---------- types ----------
export interface NorwayColumnsData {
  // Table 1 — LC fields + extra fields
  containerId?: string;
  trainNumber?: string;
  togref?: string;
  rutekode?: string;
  turnummer?: number | null;
  // Table 2 — volume fields + leg/extra fields
  bpx?: number | null;
  pri03?: number | null;
  pri49?: number | null;
  upri03?: number | null;
  upri49?: number | null;
  parti?: number | null;
  brev?: number | null;
  pru?: number | null;
  pip?: number | null;
  tom?: boolean;
  adr?: string | null;
  // Textarea — extra field
  inneholdsbeskrivelse?: string;
  // Meta
  lastUpdatedBy?: string;
}

export interface EditNorwayColumnsModalProps {
  params: GridCellParams;
  /** Override the route id used for API calls (needed when params.row.id is not the numeric route id) */
  routeIdOverride?: number;
  /** Override the leg id used for API calls */
  legIdOverride?: number;
  /** Override the carDetail id used for API calls */
  carDetailIdOverride?: number;
  /** Override the row key passed back in the callback (e.g. compound string ids in TFT grid) */
  rowKeyOverride?: string | number;
  /** Override specific NorwayColumnsData fields — merged on top of params.row (e.g. for grids that use different field names) */
  initialDataOverrides?: Partial<NorwayColumnsData>;
  onNorwayColumnsUpdate?: (
    updatedFields: {
      id: string | number;
      legId?: number;
      routeId?: number;
      carDetailId?: number;
    } & NorwayColumnsData,
  ) => void;
  tableName?: string;
  noTextWrap?: boolean;
}

// ---------- helpers ----------
function toNum(v: string): number | null {
  const n = parseInt(v, 10);
  return isNaN(n) ? null : n;
}

function hasChanged<T>(a: T, b: T): boolean {
  if (a == null && b == null) return false;
  return a !== b;
}

// ---------- component ----------
const EditNorwayColumnsModal: React.FC<EditNorwayColumnsModalProps> = ({
  params,
  routeIdOverride,
  legIdOverride,
  carDetailIdOverride,
  rowKeyOverride,
  initialDataOverrides,
  onNorwayColumnsUpdate,
  noTextWrap = true,
}) => {
  const routeId: number = routeIdOverride ?? params.row.id;
  const routeName: string = params.row.routeId;
  const transportationDate: string = params.row.transportationDate;
  const carDetailId: number = carDetailIdOverride ?? params.row.carDetailId;
  const legId: number = legIdOverride ?? params.row.firstLegId;
  const rowKey: string | number = rowKeyOverride ?? params.row.id;
  const initialData: NorwayColumnsData = {
    ...(params.row as NorwayColumnsData),
    ...initialDataOverrides,
  };
  // Use raw row value (not params.value) so newlines are preserved —
  // the valueGetter replaces \n with ' ; ' for filtering/export only.
  const rawFieldValue = params.row[params.field];
  const cellValue: string = rawFieldValue != null ? String(rawFieldValue) : '';

  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState<NorwayColumnsData>(initialData);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Skip incoming subscription updates while the user is editing.
  const isDirtyRef = useRef(false);
  // const terminalId: number =
  //   (params.row as { terminalId?: number }).terminalId ?? 0;
  const locationId: number =
    (params.row as { locationId?: number }).locationId ?? 0;

  const { globalDateFormat } = useUserConfiguration();

  const [updateExtraColumns] = useUpdateExtraColumnsMutation();
  const [updateVolumeDetails] = useUpdateVolumeDetailsMutation();
  const [updateCarTrailerDetails] = useUpdateCarTrailerDetailsMutation();
  const [updateLegDetails] = useUpdateLegDetailsMutation();

  // Live updates while the modal is open; Apollo unsubscribes when skip flips.
  useTerminalStatusTruckFillAndTimeUpdateSubscription({
    variables: { locationId: locationId || 0 },
    skip: !open || locationId === 0 || !legId,
    onSubscriptionData({ subscriptionData }) {
      // Never overwrite local state mid-edit (causes focus loss / freezes).
      if (isDirtyRef.current) return;

      const payload = subscriptionData.data?.truckFillAndTimeUpdate;
      if (!payload || payload.refreshWholeGrid !== false) return;

      const subRouteLeg = payload.routeLeg;
      if (!subRouteLeg || subRouteLeg.id !== legId) return;

      const volume = subRouteLeg.carDetails?.loadingListItems?.[0]?.volume;
      const carDetails = subRouteLeg.carDetails;
      const route = subRouteLeg.route;

      setLocal((prev) => {
        const next: NorwayColumnsData = {
          ...prev,
          bpx: volume?.bpx ?? undefined,
          pri03: volume?.pri03 ?? undefined,
          pri49: volume?.pri49 ?? undefined,
          upri03: volume?.upri03 ?? undefined,
          upri49: volume?.upri49 ?? undefined,
          parti: volume?.parti ?? undefined,
          brev: volume?.brev ?? undefined,
          pru: volume?.pru ?? undefined,
          pip: volume?.pip ?? undefined,
          togref: carDetails?.togref ?? undefined,
          containerId: carDetails?.containerId ?? undefined,
          rutekode: route?.rutekode ?? undefined,
          turnummer: route?.turnummer ?? undefined,
          trainNumber: route?.trainNumber ?? undefined,
          adr: route?.adr ?? undefined,
          inneholdsbeskrivelse: route?.inneholdsbeskrivelse ?? undefined,
          tom: subRouteLeg.tom ?? undefined,
          lastUpdatedBy: subRouteLeg.lastUpdatedBy ?? prev.lastUpdatedBy,
        };

        // Shallow-compare so React skips a re-render when nothing changed.
        let changed = false;
        const keys = Object.keys(next) as (keyof NorwayColumnsData)[];
        for (const k of keys) {
          if (next[k] !== prev[k]) {
            changed = true;
            break;
          }
        }
        return changed ? next : prev;
      });
    },
  });

  // Reset local state to latest initialData each time modal opens
  const handleOpen = () => {
    setLocal(initialData);
    setSaveError(null);
    isDirtyRef.current = false;
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSaveError(null);
    isDirtyRef.current = false;
  };

  const handleFieldChange = useCallback(
    (
      field: keyof NorwayColumnsData,
      value: NorwayColumnsData[keyof NorwayColumnsData],
    ) => {
      isDirtyRef.current = true;
      setLocal((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  // ---------- save ----------
  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const orig = initialData;
      const promises: Promise<any>[] = [];

      // --- Group 1: extraColumns (turnummer, rutekode, trainNumber, inneholdsbeskrivelse, adr) ---
      const extraChanged: Record<string, any> = {};
      if (hasChanged(local.turnummer, orig.turnummer))
        extraChanged.turnummer = local.turnummer;
      if (hasChanged(local.rutekode, orig.rutekode))
        extraChanged.rutekode = local.rutekode;
      if (hasChanged(local.trainNumber, orig.trainNumber))
        extraChanged.trainNumber = local.trainNumber;
      if (hasChanged(local.inneholdsbeskrivelse, orig.inneholdsbeskrivelse))
        extraChanged.inneholdsbeskrivelse = local.inneholdsbeskrivelse;
      if (hasChanged(local.adr, orig.adr)) extraChanged.adr = local.adr;
      if (Object.keys(extraChanged).length > 0) {
        promises.push(
          updateExtraColumns({
            variables: {
              id: routeId,
              extraColumnData: extraChanged,
            },
          }),
        );
      }

      // --- Group 2: volumeDetails (bpx, pri03, pri49, upri03, upri49, parti, brev, pru, pip) ---
      const volumeChanged: Record<string, any> = { id: carDetailId };
      let anyVolumeChanged = false;
      const volumeFields: Array<keyof NorwayColumnsData> = [
        'bpx',
        'pri03',
        'pri49',
        'upri03',
        'upri49',
        'parti',
        'brev',
        'pru',
        'pip',
      ];
      volumeFields.forEach((f) => {
        if (hasChanged(local[f], orig[f])) {
          volumeChanged[f] = local[f];
          anyVolumeChanged = true;
        }
      });
      if (anyVolumeChanged) {
        promises.push(
          updateVolumeDetails({
            variables: { input: volumeChanged as any },
          }),
        );
      }

      // --- Group 3: carTrailerDetails (togref, containerId) ---
      const lcChanged: Record<string, any> = {
        id: carDetailId,
        legId,
        type: TaskName.Car,
      };
      let anyLCChanged = false;
      if (hasChanged(local.togref, orig.togref)) {
        lcChanged.togref = local.togref;
        anyLCChanged = true;
      }
      if (hasChanged(local.containerId, orig.containerId)) {
        lcChanged.containerId = local.containerId;
        anyLCChanged = true;
      }
      if (anyLCChanged) {
        promises.push(
          updateCarTrailerDetails({
            variables: { input: lcChanged as any },
          }),
        );
      }

      // --- Group 4: legDetails (tom) ---
      if (hasChanged(local.tom, orig.tom)) {
        promises.push(
          updateLegDetails({
            variables: {
              input: {
                id: legId,
                tom: local.tom,
              } as any,
            },
          }),
        );
      }

      await Promise.all(promises);

      isDirtyRef.current = false;
      handleClose();
      onNorwayColumnsUpdate?.({
        id: rowKey,
        legId,
        routeId,
        carDetailId,
        ...local,
      });
    } catch (err: any) {
      setSaveError(err?.message || 'Save failed. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // --- format transportationDate for header ---
  const formattedTransportDate = (() => {
    try {
      const dateFormat = globalDateFormat?.split(' ')[0] || 'yyyy-MM-dd';
      return format(parseISO(transportationDate), dateFormat);
    } catch {
      return transportationDate;
    }
  })();

  const cellSx = { py: 0.75, px: 1 };
  const headerCellSx = {
    fontWeight: 700,
    fontSize: '0.72rem',
    py: 1,
    px: 1,
    color: '#555',
    whiteSpace: 'nowrap' as const,
    backgroundColor: '#f5f5f5',
  };
  const inputSx = { minWidth: 80 };
  const numInputProps = { style: { textAlign: 'right' as const } };

  return (
    <>
      {/* Cell trigger */}
      <Box
        onClick={handleOpen}
        sx={{
          cursor: 'pointer',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          textAlign: 'left',
        }}
      >
        {typeof params.value === 'boolean' ? (
          <Checkbox
            checked={params.value}
            size="small"
            disableRipple
            sx={{ padding: 0, pointerEvents: 'none' }}
          />
        ) : (
          <Typography
            variant="body2"
            noWrap={
              params.field === 'inneholdsbeskrivelse' ? false : noTextWrap
            }
            sx={
              params.field === 'rutekode'
                ? { textTransform: 'uppercase' }
                : params.field === 'inneholdsbeskrivelse' || !noTextWrap
                ? { whiteSpace: 'pre-line' }
                : undefined
            }
          >
            {cellValue || ''}
          </Typography>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: 400 } }}
      >
        {/* ---- Header ---- */}
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            py: 1.5,
            px: 3,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Box>
            <Typography
              component="div"
              sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 0.25 }}
            >
              {routeName} | {formattedTransportDate} |{' '}
              <strong style={{ color: '#000', fontWeight: 700 }}>
                EDIT CONTAINER DETAILS NO
              </strong>
            </Typography>
            {initialData.lastUpdatedBy && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
              >
                LAST UPDATED BY: {initialData.lastUpdatedBy.toUpperCase()}
              </Typography>
            )}
          </Box>

          {/* ✕ / ✓ buttons */}
          <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
            <IconButton
              onClick={handleClose}
              size="small"
              aria-label="Discard changes"
              sx={{
                backgroundColor: '#9e9e9e',
                color: '#fff',
                borderRadius: 1,
                '&:hover': { backgroundColor: '#757575' },
              }}
            >
              <CloseIcon />
            </IconButton>
            <IconButton
              onClick={handleSave}
              size="small"
              disabled={isSaving}
              aria-label="Save container details"
              sx={{
                backgroundColor: '#4caf50',
                color: '#fff',
                borderRadius: 1,
                '&:hover': { backgroundColor: '#388e3c' },
                '&.Mui-disabled': { backgroundColor: '#a5d6a7', color: '#fff' },
              }}
            >
              {isSaving ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <CheckIcon />
              )}
            </IconButton>
          </Box>
        </DialogTitle>

        {/* ---- Content ---- */}
        <DialogContent sx={{ px: 3, pt: 2, pb: 3 }}>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}

          {/* ---- Table 1: CONTAINER ID · TRAIN NUMBER · TRAIN REFERENCE · RUTEKODE · TURNUMMER ---- */}
          <Table
            size="small"
            sx={{ mb: 3, '& .MuiTableCell-root': { borderColor: '#e0e0e0' } }}
          >
            <TableHead>
              <TableRow>
                {[
                  { label: 'CONTAINER ID', disabled: false },
                  { label: 'TRAIN NUMBER', disabled: false },
                  { label: 'TRAIN REFERENCE', disabled: false },
                  { label: 'RUTEKODE', disabled: false },
                  { label: 'TURNUMMER', disabled: false },
                ].map(({ label, disabled }) => (
                  <TableCell
                    key={label}
                    sx={{
                      ...headerCellSx,
                      ...(disabled ? { color: '#bdbdbd' } : {}),
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ '& td': cellSx }}>
                <TableCell>
                  <TextField
                    size="small"
                    value={local.containerId ?? ''}
                    onChange={(e) =>
                      handleFieldChange(
                        'containerId',
                        e.target.value.toUpperCase(),
                      )
                    }
                    inputProps={{
                      maxLength: 50,
                      style: { textTransform: 'uppercase' },
                    }}
                    sx={inputSx}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={local.trainNumber ?? ''}
                    onChange={(e) =>
                      handleFieldChange('trainNumber', e.target.value)
                    }
                    inputProps={{ maxLength: 50 }}
                    sx={inputSx}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={local.togref ?? ''}
                    onChange={(e) =>
                      handleFieldChange('togref', e.target.value)
                    }
                    inputProps={{ maxLength: 50 }}
                    sx={inputSx}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={local.rutekode ?? ''}
                    onChange={(e) =>
                      handleFieldChange(
                        'rutekode',
                        e.target.value.toUpperCase(),
                      )
                    }
                    inputProps={{
                      maxLength: 50,
                      style: { textTransform: 'uppercase' },
                    }}
                    sx={inputSx}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={local.turnummer ?? ''}
                    onChange={(e) =>
                      handleFieldChange(
                        'turnummer',
                        e.target.value === '' ? null : toNum(e.target.value),
                      )
                    }
                    inputProps={{ ...numInputProps, min: 0 }}
                    sx={{ minWidth: 90 }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* ---- Table 2: BPX · PRI 0-3 · PRI 4-9 · UPRI 0-3 · UPRI 4-9 · PARTI · BREV · PRU · PIP · TOM · ADR ---- */}
          <Table
            size="small"
            sx={{ mb: 3, '& .MuiTableCell-root': { borderColor: '#e0e0e0' } }}
          >
            <TableHead>
              <TableRow>
                {[
                  'BPX',
                  'PRI 0-3',
                  'PRI 4-9',
                  'UPRI 0-3',
                  'UPRI 4-9',
                  'PARTI',
                  'BREV',
                  'PRU',
                  'PIP',
                  'TOM',
                  'ADR',
                ].map((h) => (
                  <TableCell key={h} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ '& td': cellSx }}>
                {(
                  [
                    'bpx',
                    'pri03',
                    'pri49',
                    'upri03',
                    'upri49',
                    'parti',
                    'brev',
                    'pru',
                    'pip',
                  ] as Array<keyof NorwayColumnsData>
                ).map((field) => (
                  <TableCell key={field}>
                    <TextField
                      size="small"
                      type="number"
                      value={local[field] ?? ''}
                      onChange={(e) =>
                        handleFieldChange(
                          field,
                          e.target.value === '' ? null : toNum(e.target.value),
                        )
                      }
                      inputProps={{ ...numInputProps, min: 0 }}
                      sx={{ minWidth: 70 }}
                    />
                  </TableCell>
                ))}
                {/* TOM — boolean checkbox */}
                <TableCell>
                  <Checkbox
                    checked={!!local.tom}
                    onChange={(e) => handleFieldChange('tom', e.target.checked)}
                    size="small"
                  />
                </TableCell>
                {/* ADR — YES / NO select */}
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select
                      value={local.adr ?? ''}
                      onChange={(e) =>
                        handleFieldChange('adr', e.target.value || null)
                      }
                      displayEmpty
                    >
                      <MenuItem value="">—</MenuItem>
                      <MenuItem value="YES">YES</MenuItem>
                      <MenuItem value="NO">NO</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* ---- Textarea: INNEHOLDSBESKRIVNING ---- */}
          <Box>
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                color: '#555',
                mb: 0.5,
                textTransform: 'uppercase',
              }}
            >
              Inneholdsbeskrivning
            </Typography>
            <TextField
              multiline
              minRows={4}
              fullWidth
              size="small"
              value={local.inneholdsbeskrivelse ?? ''}
              onChange={(e) =>
                handleFieldChange('inneholdsbeskrivelse', e.target.value)
              }
              placeholder="NOTE"
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditNorwayColumnsModal;
